import React, { useEffect, useState } from 'react';
import { Users, Ticket, MapPin, DollarSign, TrendingUp, TrendingDown } from 'lucide-react';
import { StatsCard } from '../components/dashboard/StatsCard';
import { RevenueChart } from '../components/dashboard/RevenueChart';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import { ApiService } from '../services/api';
import { DashboardStats, RevenueData } from '../types';
import toast from 'react-hot-toast';

export const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [revenueData, setRevenueData] = useState<RevenueData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [statsResult] = await Promise.all([
        ApiService.getDashboardStats(),
      ]);

      if (statsResult.success && statsResult.data) {
        setStats(statsResult.data);
        
        // Load real revenue data from Firebase
        const revenueResult = await ApiService.getRevenueData('7d');
        if (revenueResult.success && revenueResult.data) {
          setRevenueData(revenueResult.data);
        } else {
          // Fallback to empty data if real data fails
          setRevenueData([]);
        }
      } else {
        toast.error(statsResult.error || 'Failed to load dashboard data');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12">
        <p className="text-secondary-500">Failed to load dashboard data</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-secondary-900">Dashboard</h1>
        <p className="text-secondary-600">Welcome to Wote InterCity Admin Dashboard</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Users"
          value={stats.totalUsers.toLocaleString()}
          change={{ value: stats.userGrowth, type: stats.userGrowth >= 0 ? 'increase' : 'decrease' }}
          icon={Users}
          iconColor="text-blue-600"
          iconBgColor="bg-blue-100"
        />
        <StatsCard
          title="Total Bookings"
          value={stats.totalBookings.toLocaleString()}
          change={{ value: stats.bookingGrowth, type: stats.bookingGrowth >= 0 ? 'increase' : 'decrease' }}
          icon={Ticket}
          iconColor="text-green-600"
          iconBgColor="bg-green-100"
        />
        <StatsCard
          title="Active Routes"
          value={stats.totalRoutes.toLocaleString()}
          icon={MapPin}
          iconColor="text-purple-600"
          iconBgColor="bg-purple-100"
        />
        <StatsCard
          title="Total Revenue"
          value={`₵${stats.totalRevenue.toLocaleString()}`}
          change={{ value: stats.revenueGrowth, type: stats.revenueGrowth >= 0 ? 'increase' : 'decrease' }}
          icon={DollarSign}
          iconColor="text-yellow-600"
          iconBgColor="bg-yellow-100"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RevenueChart data={revenueData} />
        
        <Card>
          <CardHeader>
            <CardTitle>Booking Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                  <span className="text-sm font-medium">Active</span>
                </div>
                <span className="text-sm font-semibold">{stats.activeBookings}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                  <span className="text-sm font-medium">Completed</span>
                </div>
                <span className="text-sm font-semibold">{stats.completedBookings}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-orange-500 rounded-full mr-3"></div>
                  <span className="text-sm font-medium">Pending</span>
                </div>
                <span className="text-sm font-semibold">{stats.pendingBookings}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-red-500 rounded-full mr-3"></div>
                  <span className="text-sm font-medium">Cancelled</span>
                </div>
                <span className="text-sm font-semibold">{stats.cancelledBookings}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-2 border-b border-secondary-100">
              <div>
                <p className="text-sm font-medium">New user registered</p>
                <p className="text-xs text-secondary-500">2 minutes ago</p>
              </div>
              <TrendingUp className="h-4 w-4 text-green-500" />
            </div>
            <div className="flex items-center justify-between py-2 border-b border-secondary-100">
              <div>
                <p className="text-sm font-medium">Booking completed</p>
                <p className="text-xs text-secondary-500">5 minutes ago</p>
              </div>
              <TrendingUp className="h-4 w-4 text-green-500" />
            </div>
            <div className="flex items-center justify-between py-2 border-b border-secondary-100">
              <div>
                <p className="text-sm font-medium">Route updated</p>
                <p className="text-xs text-secondary-500">10 minutes ago</p>
              </div>
              <TrendingUp className="h-4 w-4 text-blue-500" />
            </div>
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="text-sm font-medium">Payment failed</p>
                <p className="text-xs text-secondary-500">15 minutes ago</p>
              </div>
              <TrendingDown className="h-4 w-4 text-red-500" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
